import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalState } from '../contexts/GlobalContext';
import { apiFetch } from '../utils/api';
import '../Stylesheets/Pipeline.css';

const STAGES = [
  'Attempting Contact',
  'Connected',
  'Qualified',
  'Proposal or Showing',
  'Negotiation',
  'Under Contract',
  'Closed',
  'Lost',
];

const normalizeStage = (stage) => {
  if (!stage) return 'New';
  const matched = STAGES.find((candidate) => candidate.toLowerCase() === String(stage).toLowerCase());
  return matched || 'New';
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
    : value;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const daysInStage = (contact) => {
  const raw = contact.stage_changed_at || contact.createdAt;
  if (!raw) return 0;
  const timestamp = new Date(raw).getTime();
  if (Number.isNaN(timestamp)) return 0;
  return Math.max(0, Math.floor((Date.now() - timestamp) / 86400000));
};

const nextOpenTask = (contact) => (contact.tasks || [])
  .filter((task) => String(task.status || '').toLowerCase() !== 'closed')
  .sort((a, b) => new Date(a.dueDate || a.due_date || 8640000000000000) - new Date(b.dueDate || b.due_date || 8640000000000000))[0];

function Pipeline() {
  const { state, fetchContacts } = useGlobalState();
  const [contacts, setContacts] = useState(state.contacts || []);
  const [draggedId, setDraggedId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [stageToAddTo, setStageToAddTo] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState('');

  useEffect(() => {
    if (state.user?.id && state.contacts.length === 0) fetchContacts();
  }, [state.user, state.contacts.length, fetchContacts]);

  useEffect(() => setContacts(state.contacts || []), [state.contacts]);

  const grouped = useMemo(() => STAGES.reduce((result, stage) => {
    result[stage] = contacts.filter((contact) => normalizeStage(contact.prospect_stage) === stage);
    return result;
  }, {}), [contacts]);

  const availableContacts = stageToAddTo
    ? contacts
      .filter((contact) => normalizeStage(contact.prospect_stage) !== stageToAddTo)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    : [];

  const moveContact = async (contactId, stage) => {
    const current = contacts.find((contact) => String(contact.id) === String(contactId));
    if (!current || normalizeStage(current.prospect_stage) === stage) return false;

    const changedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const priorContacts = contacts;
    setError('');
    setSavingId(contactId);
    setContacts((items) => items.map((contact) => (
      String(contact.id) === String(contactId)
        ? { ...contact, prospect_stage: stage, stage_changed_at: changedAt }
        : contact
    )));

    try {
      await apiFetch('editContact', {
        method: 'POST',
        body: JSON.stringify({
          contact_id: contactId,
          prospect_stage: stage,
          stage_changed_at: changedAt,
          won_at: stage === 'Closed' ? changedAt : null,
          lost_at: stage === 'Lost' ? changedAt : null,
        }),
      });
      await fetchContacts();
      return true;
    } catch (err) {
      setContacts(priorContacts);
      setError(err.message || 'Unable to update the contact stage.');
      return false;
    } finally {
      setSavingId(null);
      setDraggedId(null);
    }
  };

  const openAddContactDialog = (stage) => {
    setStageToAddTo(stage);
    setSelectedContactId('');
  };

  const addContactToStage = async (event) => {
    event.preventDefault();
    if (!stageToAddTo || !selectedContactId) return;

    const wasMoved = await moveContact(selectedContactId, stageToAddTo);
    if (wasMoved) setStageToAddTo(null);
  };

  return (
    <div className="pipeline-page py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 className="h2 mb-1">Sales Pipeline</h1>
          <p className="text-muted mb-0">Drag a contact card to update its prospect stage.</p>
        </div>
        <Link to="/form" state={{ role: 'contact' }} className="btn btn-success">+ Add Contact</Link>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="pipeline-board" aria-label="Contact pipeline">
        {STAGES.map((stage) => (
          <section
            className="pipeline-column"
            key={stage}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => draggedId && moveContact(draggedId, stage)}
          >
            <header className="pipeline-column-header">
              <span>{stage}</span>
              <div className="d-flex align-items-center gap-2">
                <span className="badge rounded-pill text-bg-light">{grouped[stage].length}</span>
                <button
                  type="button"
                  className="pipeline-add-contact"
                  onClick={() => openAddContactDialog(stage)}
                  aria-label={`Add a contact to ${stage}`}
                  title={`Add a contact to ${stage}`}
                >
                  +
                </button>
              </div>
            </header>
            <div className="pipeline-card-list">
              {grouped[stage].map((contact) => {
                const task = nextOpenTask(contact);
                return (
                  <article
                    className={`pipeline-card ${savingId === contact.id ? 'is-saving' : ''}`}
                    key={contact.id}
                    draggable={savingId !== contact.id}
                    onDragStart={() => setDraggedId(contact.id)}
                    onDragEnd={() => setDraggedId(null)}
                  >
                    <div className="d-flex justify-content-between gap-2">
                      <Link to={`/contact/${contact.id}`} className="pipeline-contact-name">{contact.name || 'Unnamed contact'}</Link>
                      {contact.priority && <span className={`pipeline-priority priority-${String(contact.priority).toLowerCase()}`}>{contact.priority}</span>}
                    </div>
                    {contact.company_name && <div className="pipeline-company">{contact.company_name}</div>}
                    <dl className="pipeline-card-details">
                      <div><dt>Value</dt><dd>{formatMoney(contact.estimated_value)}</dd></div>
                      <div><dt>Next task</dt><dd>{task ? `${task.taskType || task.task_type || 'Task'} · ${formatDate(task.dueDate || task.due_date)}` : '—'}</dd></div>
                      <div><dt>Last contact</dt><dd>{formatDate(contact.last_contacted_at)}</dd></div>
                      <div><dt>Follow-up</dt><dd>{formatDate(contact.next_follow_up_at)}</dd></div>
                    </dl>
                    <div className="pipeline-card-footer">{daysInStage(contact)} day{daysInStage(contact) === 1 ? '' : 's'} in stage</div>
                  </article>
                );
              })}
              {grouped[stage].length === 0 && <div className="pipeline-empty">Drop contacts here</div>}
            </div>
          </section>
        ))}
      </div>

      {stageToAddTo && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="add-contact-to-stage-title">
            <div className="modal-dialog">
              <form className="modal-content" onSubmit={addContactToStage}>
                <div className="modal-header">
                  <h2 className="modal-title fs-5" id="add-contact-to-stage-title">Add Contact to {stageToAddTo}</h2>
                  <button type="button" className="btn-close" onClick={() => setStageToAddTo(null)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <label className="form-label" htmlFor="pipeline-contact-select">Contact</label>
                  <select
                    className="form-select"
                    id="pipeline-contact-select"
                    value={selectedContactId}
                    onChange={(event) => setSelectedContactId(event.target.value)}
                    disabled={savingId !== null || availableContacts.length === 0}
                    required
                  >
                    <option value="">Select a contact</option>
                    {availableContacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name || 'Unnamed contact'}{contact.company_name ? ` - ${contact.company_name}` : ''}
                      </option>
                    ))}
                  </select>
                  {availableContacts.length === 0 && <p className="text-muted mb-0 mt-2">All contacts are already in this stage.</p>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setStageToAddTo(null)} disabled={savingId !== null}>Cancel</button>
                  <button type="submit" className="btn btn-success" disabled={!selectedContactId || savingId !== null}>Add to Stage</button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}

export default Pipeline;
